import { AbstractDate } from "@linkfluence/models/date";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { SearchFragmentUsers, SortInfluencer } from "@radarly/models/search";
import { Card, UsersCard } from "..";
import { isUsersCardDataviz } from "../dataviz";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";

interface UsersCardFactoryOptions {
  from?: AbstractDate;
  to?: AbstractDate;
  clause?: Clause;
  sortBy?: SortInfluencer;
}

export class UsersCardFragmentsFactory extends CardFragmentsFactory {
  static create(input?: UsersCardFactoryOptions) {
    const factory = new UsersCardFragmentsFactory();
    return factory.getFragments(input);
  }
  static createFromCard(card: Card, input?: UsersCardFactoryOptions) {
    const factory = new UsersCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return factory.getFragments({
      clause: factory.getClauseFromCard(card),
      from,
      to,
      sortBy: input?.sortBy, // @TODO
    });
  }
  private getFragments(input?: UsersCardFactoryOptions): [SearchFragmentUsers] {
    return [this.getFragment(input)];
  }
  private getFragment(input?: UsersCardFactoryOptions): SearchFragmentUsers {
    return {
      request: {
        query: this.getQuery(input),
        aggs: {
          users: {
            metrics: [Metric.Engagement, Metric.Reach, Metric.Doc],
            size: 20,
            sort: {
              on: input?.sortBy || Metric.Engagement,
              order: "desc",
            },
          },
        },
      },
    };
  }
}

export class UsersCardFactory
  implements ICardFactory<UsersCard, UsersCardFactoryOptions>
{
  public static getInstance() {
    return new UsersCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: UsersCardFactoryOptions & {
      dataviz?: UsersCard["dataviz"];
    }
  ) {
    return CardFactory.getInstance<UsersCard>().createCard(
      workspaceId,
      projectFocuses,
      "users",
      card.title,
      UsersCardFragmentsFactory.createFromCard(card, input),
      this.createDataviz(input?.dataviz || card.dataviz)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: UsersCardFactoryOptions & {
      dataviz?: Dataviz["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<UsersCard>().createCard(
      workspaceId,
      projectFocuses,
      "users",
      title,
      UsersCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(input?.dataviz || DatavizName.TopAuthors)
      )
    );
  }
  public createDataviz(dataviz: Dataviz) {
    if (isUsersCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(DatavizName.TopAuthors) as UsersCard["dataviz"];
    }
  }
}
